import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { ModuleHeader, ConfirmDialog } from 'components/modals';
import type { ActionButton } from 'components/modals';
import { SyutGrid, type SyutGridColumn, type ActionMenuItem, MinimizedModalStrip } from 'components/ui';
import notificationService from '../../../../services/notificationService';
import { clientApi, ClientDto, ClientUpsertRequest } from 'api/services/timeTrackerApi';
import { useUnsavedChangesGuard } from 'hooks/useUnsavedChangesGuard';
import { useModalMinimize } from 'contexts/ModalMinimizeContext';

const blankForm: ClientUpsertRequest = {
  name: '', contactEmail: '', contactPhone: '', address: '', isActive: true,
};

interface ClientMasterScreenProps {
  moduleIcon?: string;
  tabId?: string;
  moduleName?: string;
}

const ClientMasterScreen: React.FC<ClientMasterScreenProps> = ({ tabId, moduleName }) => {
  const { minimize, isMinimized, dismiss: dismissMinimized, getMinimizedModal } = useModalMinimize();
  const [rows, setRows] = useState<ClientDto[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [includeInactive, setIncludeInactive] = useState(false);
  const [filterOpen, setFilterOpen] = useState(false);

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<ClientDto | null>(null);
  const [form, setForm] = useState<ClientUpsertRequest>(blankForm);
  const [saving, setSaving] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<ClientDto | null>(null);

  // Minimize plumbing — snapshot + refs used by handleMinimize's onRestore.
  const formRef = useRef(form);           formRef.current = form;
  const editingRef = useRef(editing);     editingRef.current = editing;
  const initialSnapshotRef = useRef<string>('');
  const isRestoreRef = useRef(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!formOpen) return;
    if (isRestoreRef.current) { isRestoreRef.current = false; return; }
    initialSnapshotRef.current = JSON.stringify(form);
    // Only re-baseline on open transitions, not on every keystroke.
  }, [formOpen]);

  const { discardOpen, requestClose, confirmDiscard, cancelDiscard } =
    useUnsavedChangesGuard(formOpen, form, () => !saving && setFormOpen(false));

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await clientApi.list({
        page: page + 1,
        pageSize,
        searchTerm: searchQuery || undefined,
        includeInactive: includeInactive || undefined,
      });
      setRows(res.items);
      setTotalCount(res.totalCount);
    } catch (err: any) {
      notificationService.toast.error(err?.response?.data?.message || err.message || 'Failed to load clients.');
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, searchQuery, includeInactive]);

  useEffect(() => {
    const t = setTimeout(() => { load(); }, 350);
    return () => clearTimeout(t);
  }, [load]);

  const openAdd = useCallback(() => {
    // If an Add modal is already minimized, restore it instead of opening fresh.
    const modalId = tabId ? `${tabId}-form-modal-create-1` : null;
    if (modalId && isMinimized(modalId)) {
      const modal = getMinimizedModal(modalId);
      dismissMinimized(modalId);
      modal?.onRestore?.();
      return;
    }
    setEditing(null);
    setForm(blankForm);
    setFieldErrors({});
    setFormOpen(true);
  }, [tabId, isMinimized, dismissMinimized, getMinimizedModal]);

  const openEdit = useCallback((row: ClientDto) => {
    const modalId = tabId ? `${tabId}-form-modal-edit-${row.id}` : null;
    if (modalId && isMinimized(modalId)) {
      const modal = getMinimizedModal(modalId);
      dismissMinimized(modalId);
      modal?.onRestore?.();
      return;
    }
    setEditing(row);
    setForm({
      name: row.name,
      contactEmail: row.contactEmail ?? '',
      contactPhone: row.contactPhone ?? '',
      address: row.address ?? '',
      isActive: row.isActive,
    });
    setFieldErrors({});
    setFormOpen(true);
  }, [tabId, isMinimized, dismissMinimized, getMinimizedModal]);

  const handleMinimize = useCallback(() => {
    if (!tabId || !moduleName) return;
    const currentForm = { ...formRef.current };
    const currentEditing = editingRef.current;
    const snapshot = initialSnapshotRef.current;
    const modalId = currentEditing
      ? `${tabId}-form-modal-edit-${currentEditing.id}`
      : `${tabId}-form-modal-create-1`;
    const nameStr = currentForm.name?.trim();
    const title = currentEditing
      ? `Edit - ${currentEditing.name}`
      : (nameStr ? `Add - ${nameStr} (1)` : `Add New (1)`);
    const isDirty = snapshot ? JSON.stringify(currentForm) !== snapshot : false;

    minimize({
      id: modalId,
      title,
      moduleName,
      tabId,
      isDirty,
      onRestore: () => {
        isRestoreRef.current = true;
        setEditing(currentEditing);
        setForm(currentForm);
        initialSnapshotRef.current = snapshot;
        setFormOpen(true);
      },
    });
    setFormOpen(false);
  }, [tabId, moduleName, minimize]);

  const validateForm = (): boolean => {
    const errs: Record<string, string> = {};
    if (!form.name?.trim()) errs.name = 'Name is required';
    if (form.contactEmail && !/.+@.+\..+/.test(form.contactEmail)) errs.contactEmail = 'Invalid email';
    setFieldErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const save = async () => {
    if (!validateForm()) return;
    setSaving(true);
    try {
      if (editing) {
        await clientApi.update(editing.id, { ...form, rowVersion: editing.rowVersion });
        notificationService.toast.success(`Client "${form.name}" updated.`);
      } else {
        await clientApi.create(form);
        notificationService.toast.success(`Client "${form.name}" created.`);
      }
      setFormOpen(false);
      setEditing(null);
      await load();
    } catch (err: any) {
      notificationService.toast.error(err?.response?.data?.message || 'Save failed.');
    } finally {
      setSaving(false);
    }
  };

  const requestDelete = useCallback((row: ClientDto) => {
    setPendingDelete(row);
    setDeleteOpen(true);
  }, []);

  const confirmDelete = useCallback(async () => {
    if (!pendingDelete) return;
    setDeleting(true);
    try {
      await clientApi.remove(pendingDelete.id, pendingDelete.rowVersion);
      notificationService.toast.success(`"${pendingDelete.name}" deleted.`);
      setDeleteOpen(false);
      setPendingDelete(null);
      await load();
    } catch (err: any) {
      notificationService.toast.error(err?.response?.data?.message || 'Delete failed.');
    } finally {
      setDeleting(false);
    }
  }, [pendingDelete, load]);

  type GridRow = ClientDto & { id: string };
  const columns: SyutGridColumn<GridRow>[] = useMemo(() => [
    { id: 'name',         header: 'Client Name', accessorKey: 'name',         size: 240 },
    { id: 'contactEmail', header: 'Email',       accessorKey: 'contactEmail', size: 220,
      cell: ({ getValue }) => (getValue() as string) || '—' },
    { id: 'contactPhone', header: 'Phone',       accessorKey: 'contactPhone', size: 150,
      cell: ({ getValue }) => (getValue() as string) || '—' },
    { id: 'address',      header: 'Address',     accessorKey: 'address',      size: 240, enableSorting: false,
      cell: ({ getValue }) => (getValue() as string) || '—' },
    { id: 'isActive',     header: 'Status',      accessorKey: 'isActive',     size: 100,
      cell: ({ getValue }) => (
        <span className={`badge ${getValue() ? 'badge-active' : 'badge-inactive'}`}>
          {getValue() ? 'Active' : 'Inactive'}
        </span>
      ),
    },
  ], []);

  const rowActions = useCallback((row: GridRow): ActionMenuItem[] => [
    { id: 'edit',   label: 'Edit',   icon: <IconSystem.edit  size={16} />, onClick: () => openEdit(row) },
    { id: 'delete', label: 'Delete', icon: <IconSystem.trash size={16} />, onClick: () => requestDelete(row), variant: 'danger' },
  ], [openEdit, requestDelete]);

  const actions: ActionButton[] = [
    { id: 'add',     icon: <IconSystem.add     size={18} />, label: 'Add Client', onClick: openAdd, tooltip: 'Add new client', variant: 'icon' },
    { id: 'refresh', icon: <IconSystem.refresh size={18} />, label: 'Refresh',    onClick: load,    tooltip: 'Refresh',         variant: 'icon' },
  ];

  return (
    <div className="module-master-container">
      <ModuleHeader
        title="Client Master"
        description="Manage clients tracked by the time-tracker module."
        icon={<IconSystem.users size={18} />}
        searchConfig={{ value: searchQuery, onChange: setSearchQuery, placeholder: 'Search clients…' }}
        filterConfig={{ onClick: () => setFilterOpen(o => !o), isActive: filterOpen }}
        actions={actions}
        leadingToolbarItems={tabId ? <MinimizedModalStrip tabId={tabId} /> : undefined}
      />

      <div className="module-content" style={{ marginTop: '6px' }}>
        <div className="data-grid-container">
          <SyutGrid
            data={rows as GridRow[]}
            columns={columns}
            enableRowSelection
            enablePagination
            manualPagination
            totalCount={totalCount}
            currentPage={page}
            pageSize={pageSize}
            onPageChange={setPage}
            onPageSizeChange={(s) => { setPageSize(s); setPage(0); }}
            rowActions={rowActions}
            emptyMessage={loading ? 'Loading…' : 'No clients found.'}
          />
        </div>

        {filterOpen && (
          <div className="search-panel">
            <div className="search-panel-header">
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
                <IconSystem.filter size={14} />
                <h3 className="search-panel-title">Filters</h3>
              </div>
              <button onClick={() => setFilterOpen(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '0.25rem', display: 'flex', color: 'var(--text-muted)' }}>
                <IconSystem.x size={16} />
              </button>
            </div>
            <div className="search-panel-content">
              <div className="filter-section">
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.85rem' }}>
                  <input
                    type="checkbox"
                    checked={includeInactive}
                    onChange={(e) => { setIncludeInactive(e.target.checked); setPage(0); }}
                  />
                  Include inactive clients
                </label>
              </div>
            </div>
            <div className="filter-actions">
              <button className="btn btn-secondary" onClick={() => { setIncludeInactive(false); setPage(0); }}>Clear</button>
              <button className="btn btn-primary" onClick={() => setFilterOpen(false)}>Done</button>
            </div>
          </div>
        )}
      </div>

      {formOpen && (
        <div className="modal-backdrop" onClick={requestClose}>
          <div className="modal-container modal-md" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">
                <IconSystem.users size={16} style={{ marginRight: 8 }} />
                {editing ? 'Edit Client' : 'New Client'}
              </h2>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                {tabId && moduleName && (
                  <button className="modal-header-btn" onClick={handleMinimize} title="Minimize to bar" disabled={saving}>
                    <IconSystem.chevronDown size={16} />
                  </button>
                )}
                <button className="modal-close" onClick={requestClose}>
                  <IconSystem.x size={18} />
                </button>
              </div>
            </div>
            <div className="modal-content">
              <div className="form-grid-2">
                <div className="form-group">
                  <label className="form-label">Name<span style={{ color: 'var(--danger)' }}> *</span></label>
                  <input
                    className={`form-input ${fieldErrors.name ? 'form-field-error' : ''}`}
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    maxLength={200}
                    autoFocus
                  />
                  {fieldErrors.name && <div className="form-error-message">{fieldErrors.name}</div>}
                </div>
                <div className="form-group">
                  <label className="form-label">Status</label>
                  <select
                    className="form-select"
                    value={form.isActive ? '1' : '0'}
                    onChange={(e) => setForm({ ...form, isActive: e.target.value === '1' })}
                  >
                    <option value="1">Active</option>
                    <option value="0">Inactive</option>
                  </select>
                </div>
              </div>

              <div className="form-grid-2" style={{ marginTop: '0.875rem' }}>
                <div className="form-group">
                  <label className="form-label">Contact email</label>
                  <input
                    className={`form-input ${fieldErrors.contactEmail ? 'form-field-error' : ''}`}
                    type="email"
                    value={form.contactEmail ?? ''}
                    onChange={(e) => setForm({ ...form, contactEmail: e.target.value })}
                    maxLength={255}
                  />
                  {fieldErrors.contactEmail && <div className="form-error-message">{fieldErrors.contactEmail}</div>}
                </div>
                <div className="form-group">
                  <label className="form-label">Phone</label>
                  <input
                    className="form-input"
                    value={form.contactPhone ?? ''}
                    onChange={(e) => setForm({ ...form, contactPhone: e.target.value })}
                    maxLength={50}
                  />
                </div>
              </div>

              <div className="form-group" style={{ marginTop: '0.875rem' }}>
                <label className="form-label">Address</label>
                <textarea
                  className="form-textarea"
                  rows={3}
                  value={form.address ?? ''}
                  onChange={(e) => setForm({ ...form, address: e.target.value })}
                />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={requestClose} disabled={saving}>Cancel</button>
              <button className="btn btn-primary" onClick={save} disabled={saving}>
                {saving ? 'Saving…' : (editing ? 'Update' : 'Create')}
              </button>
            </div>
          </div>
        </div>
      )}

      <ConfirmDialog
        open={deleteOpen}
        title="Delete Client"
        message={`Are you sure you want to delete "${pendingDelete?.name}"? This action cannot be undone.`}
        confirmLabel="Delete"
        cancelLabel="Cancel"
        variant="danger"
        loading={deleting}
        loadingMessage="Deleting…"
        onConfirm={confirmDelete}
        onCancel={() => { setDeleteOpen(false); setPendingDelete(null); }}
      />

      <ConfirmDialog
        open={discardOpen}
        title="Discard Changes?"
        message="You have unsaved changes. Do you want to discard them?"
        confirmLabel="Discard"
        cancelLabel="Keep Editing"
        variant="danger"
        onConfirm={confirmDiscard}
        onCancel={cancelDiscard}
      />
    </div>
  );
};

export default ClientMasterScreen;
